import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import ColunaBoard from './colunaBoard';
import { IEsteira } from '../../interfaces/IEsteira';
import { useAuth } from '../../contexts/AuthProvider';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IRelatorio } from '../../interfaces/IRelatorio';
import RelatoriosService from '../../services/RelatoriosService';
import Swal from 'sweetalert2';
import { useHistory } from 'react-router-dom';
import ModalCard from './modal-card';
import ModalExtratoBancario from './modal-extrato-bancario';
import ModalFolhaPagamento from './modal-folha-pagamento';
import { IArquivoCardEsteira } from '../../interfaces/IArquivoCardEsteira';
import ArquivoCardEsteiraService from '../../services/ArquivoCardEsteiraService';
import api from '../../services/Api';
import { subscribeToEsteira } from '../../services/CardNotificationService';
import workflowService from '../workflow-aprovacao/workflowService';
import type { JustificativaTemplate } from '../workflow-aprovacao/models';

/*Tradução */
import './../../../i18n/config';
import { useTranslation } from 'react-i18next';

const Board = ({ esteira, empresaID, connection, mostrarReferencial }: { esteira: IEsteira, empresaID: number, connection: signalR.HubConnection, mostrarReferencial: boolean }) => {

    const { usuario } = useAuth();
    const history = useHistory();
    const { t } = useTranslation();
    const [carregandoFiltro, setCarregandoFiltro] = useState<boolean>(false);
    const [filtro, setFiltro] = useState<string>('');
    const [intervalo, setIntervalo] = useState<any>(null);

    const [dados, setDados] = useState<IRelatorio[]>([]);
    const [cartaoDestacar, setCartaoDestacar] = useState<any[]>([]);
    const [mensagemSistema, setMensagemSistema] = useState<any[]>([]);

    const [movimentarDe, setMovimentarDe] = useState<string>('');
    const [movimentarPara, setMovimentarPara] = useState<string>('');
    const [grupo, setGrupo] = useState<string>('');
    const [loadingMovimentando, setLoadingMovimentando] = useState<boolean>(false);
    const [conciliando, setConciliando] = useState<boolean>(false);
    const [modalExtratoBancario, setModalExtratoBancario] = useState<{ show: boolean; cartaoId: number; cartaoCodigo: string }>({
        show: false,
        cartaoId: 0,
        cartaoCodigo: ''
    });
    const [modalFolhaPagamento, setModalFolhaPagamento] = useState<{ show: boolean; cartaoId: number; cartaoCodigo: string }>({
        show: false,
        cartaoId: 0,
        cartaoCodigo: ''
    });
    const [modal, setShowModal] = useState<any>({
        show: false,
        data: null,
        reprocessar: null
    });

    const [soxStatus, setSoxStatus] = useState<{ submetido: boolean; concilId?: number; status?: string; etapaAtual?: string; nivelRisco?: string }>({ submetido: false });

    // Empresa cadastrada no módulo Workflow SOX (grupo econômico vinculado) — só para
    // essas a entrada no Workflow (automática ou manual) é habilitada.
    const [empresaConfiguradaWorkflow, setEmpresaConfiguradaWorkflow] = useState(false);

    // Seletor de justificativa + plano de melhoria pré-definidos (catálogo de 10 opções),
    // usado tanto na entrada automática (drag a 100%) quanto no envio manual (botão).
    const [wfTemplateModal, setWfTemplateModal] = useState<{ show: boolean; card: IRelatorio | null; automatico: boolean }>({ show: false, card: null, automatico: false });
    const [wfTemplates, setWfTemplates] = useState<JustificativaTemplate[]>([]);
    const [wfTemplateId, setWfTemplateId] = useState<number | null>(null);
    const [wfJustTemplate, setWfJustTemplate] = useState('');
    const [wfEnviandoTemplate, setWfEnviandoTemplate] = useState(false);

    useEffect(() => {
        workflowService.listarTenants()
            .then(tenants => setEmpresaConfiguradaWorkflow(
                tenants.some(t => t.empresaId === empresaID && t.grupoId != null && t.status === 'ATIVA')
            ))
            .catch(() => setEmpresaConfiguradaWorkflow(false));
    }, [empresaID]);

    const showModal = useCallback((codigo, reprocessar) => {
        const acc = dados.find((item) => item.contaEmpresa === codigo);

        // Resetar e carregar status SOX ao abrir o modal
        setSoxStatus({ submetido: false });
        if (acc?.contaEmpresa) {
            api.get('workflow/conciliacoes/status-motor', {
                params: { esteiraId: esteira.id, conta: acc.contaEmpresa }
            }).then(r => setSoxStatus(r.data)).catch(() => setSoxStatus({ submetido: false }));
        }

        setShowModal({
            show: true,
            data: acc,
            updateDescription: (description: string) => {
                setDados((old: IRelatorio[]) => {
                    const value = old || [];
                    const newReference = [...value];
                    const findIndex = newReference.findIndex(f => f.contaEmpresa === acc?.contaEmpresa);
                    if (findIndex >= 0) {
                        newReference[findIndex].descricao = description;
                    }

                    return newReference;
                });

                RelatoriosService.atualizarDescricaoConta(esteira.id, acc?.contaEmpresa || "", description);
            },
            reprocessar: reprocessar ? (anexos: IArquivoCardEsteira[]) => {
                reprocessar(anexos);
                setShowModal({
                    show: false,
                    data: null,
                    reprocessar: null
                });
            } : null
        });
    }, [dados, esteira]);

    const filtroTexto = useCallback((item: IRelatorio) => {
        if (filtro) {
            return `${item.contaEmpresa}-${item.descricaoEmpresa}-${item.contaReferencial}`.toLowerCase().includes(filtro.toLowerCase()) ? item : null;
        }

        return item;
    }, [filtro]);

    const aggragateData = useCallback((newData: IRelatorio[]) => {
        setDados((old) => {
            const cp = [...old];
            const codeExist = cp.map(m => m.contaEmpresa);
            const diff = newData.filter(f => !codeExist.includes(f.contaEmpresa));
            const newer = cp.concat(diff);
            return newer;
        })
    }, [dados]);

    const onSearch = useCallback(() => {

        if (!filtro) {
            Swal.fire({
                text: 'Por favor preencha o filtro.',
                timer: 10000,
                icon: 'error',
                showConfirmButton: false
            });
            return;
        }

        setCarregandoFiltro(true);
        RelatoriosService.buscarCartaoPorFiltro(esteira.id, filtro, false, (resp) => {
            setCarregandoFiltro(false);
            aggragateData(resp);
        })
    }, [filtro, esteira, aggragateData]);

    const movimentarGrupo = useCallback(async () => {
        if (!movimentarDe || !movimentarDe || !grupo) {
            Swal.fire({
                text: 'Por favor selecione o destino de sua movimentação e o grupo.',
                timer: 10000,
                icon: 'error',
                showConfirmButton: false
            });
            return;
        }

        setLoadingMovimentando(true);
        const result = await RelatoriosService.movimentarStatus(esteira.id, movimentarDe, movimentarPara, grupo);
        setLoadingMovimentando(false);


        if (result && result.message) {
            Swal.fire({
                text: result.message,
                timer: 10000,
                icon: 'error',
                showConfirmButton: false
            });
        }
        else {
            Swal.fire({
                text: 'Será processado um lote de até 1000 cartões, após processado os cartões serão atualizados em sua tela.',
                timer: 10000,
                icon: 'success',
                showConfirmButton: false
            });
        }


    }, [esteira, movimentarDe, movimentarPara, grupo]);

    const arrastar = useCallback((codigo, status, porcentagem, destacar = false, detalhes = null) => {
        setDados((old) => {
            if (old) {
                const copy = [...old];
                const index = old?.findIndex((item) => item.contaEmpresa === codigo);
                if (index >= 0) {
                    copy[index].status = status;
                    copy[index].destacar = destacar;
                    if (porcentagem != null && porcentagem !== undefined) {
                        copy[index].porcentagemConciliado = porcentagem;
                        (copy[index] as any).porcentagemProcessamento = porcentagem;
                    }
                    if (detalhes) {
                        (copy[index] as any).etapaProcessamento = detalhes.Etapa;
                        (copy[index] as any).totalLancamentos = detalhes.TotalLancamentos;
                        (copy[index] as any).pendentesProcessamento = detalhes.Pendentes;
                        (copy[index] as any).inicioProcessamento = detalhes.Inicio;
                        (copy[index] as any).fimProcessamento = detalhes.Fim;
                        (copy[index] as any).usuarioProcessamento = detalhes.Usuario;
                        (copy[index] as any).processando = true;
                    }
                } else {
                    RelatoriosService.buscarCartao(esteira.id, codigo, (resp) => {
                        if (resp) {
                            copy.push(resp);
                        }
                    });
                }

                return copy;
            }

            return old;
        });
    }, [esteira]);

    const changeRouteConciliation = useCallback((codigo) => {
        const finded = dados?.find(item => item.contaEmpresa === codigo);

        if (finded && (finded.isClienteFornecedor || finded.isTributo)) {

            Swal.fire({
                title: t('Qual forma de conciliação você gostaria?'),
                showDenyButton: true,
                showConfirmButton: true,
                confirmButtonColor: 'green',
                cancelButtonColor: 'red',
                confirmButtonText: t('Múltiplo'),
                denyButtonText: t(`Individual`),
            }).then((result) => {
                if (result.isConfirmed) {
                    Swal.fire({
                        title: t('Deseja processar em tela ?'),
                        showDenyButton: true,
                        showConfirmButton: true,
                        confirmButtonColor: 'green',
                        cancelButtonColor: 'red',
                        confirmButtonText: 'Sim',
                        denyButtonText: `Não`,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            history.push(`/conciliacao-grupo/${empresaID}/${esteira.id}/${codigo}`);
                        } else if (result.isDenied) {
                            Swal.fire({
                                title: 'Deseja processar por nota ou valor ?',
                                showDenyButton: true,
                                showConfirmButton: true,
                                confirmButtonColor: 'green',
                                cancelButtonColor: 'red',
                                confirmButtonText: 'Nota',
                                denyButtonText: `Valor`,
                            }).then(async (result) => {
                                if (result.isConfirmed) {
                                    const sucesso = await RelatoriosService.processarSmart(esteira.id, finded.id, "nota");
                                    if (sucesso) {
                                        arrastar(finded.contaEmpresa, "Em processamento", null);
                                        Swal.fire({
                                            text: 'Inserimos seu card para processamento. Assim que concluído, iremos notifica-lo para validação das possíveis conciliações.',
                                            timer: 10000,
                                            icon: 'success',
                                            showConfirmButton: false
                                        });
                                    }
                                } else if (result.isDenied) {
                                    Swal.fire({
                                        title: 'Valor simples ou composto ?',
                                        showDenyButton: true,
                                        showConfirmButton: true,
                                        confirmButtonColor: 'green',
                                        cancelButtonColor: 'red',
                                        confirmButtonText: 'Simples',
                                        denyButtonText: `Composto`,
                                    }).then(async (resultValor) => {
                                        const tipoValor = resultValor.isConfirmed ? "valor" : "valorComposto";
                                        const sucesso = await RelatoriosService.processarSmart(esteira.id, finded.id, tipoValor);
                                        if (sucesso) {
                                            arrastar(finded.contaEmpresa, "Em processamento", null);
                                            Swal.fire({
                                                text: 'Inserimos seu card para processamento. Assim que concluído, iremos notifica-lo para validação das possíveis conciliações.',
                                                timer: 10000,
                                                icon: 'success',
                                                showConfirmButton: false
                                            });
                                        }
                                    });
                                }
                            });
                        }
                    });
                } else if (result.isDenied) {
                    history.push(`/conciliacao/${esteira.id}/${finded.id}`);
                }
            });
        } else if (finded && (finded.isBanco || finded.isCaixa)) {
            Swal.fire({
                title: t('Gostaria de realizar a conciliação bancária?'),
                showDenyButton: true,
                showConfirmButton: true,
                confirmButtonColor: 'green',
                cancelButtonColor: 'red',
                confirmButtonText: 'Sim',
                denyButtonText: `Não`,
            }).then((result) => {
                if (result.isConfirmed) {
                    history.push(`/conciliacao-bancaria/${esteira.id}/${finded.id}`);
                }
            });
        }
    }, [esteira, empresaID, history, dados, arrastar]);

    const loadDataReport = useCallback((offset: number, status: string, setLoading, setHasMore) => {
        RelatoriosService.buscarCartoes(esteira.id, status, offset, false, (data) => {
            if (!data || data.length == 0) {
                setHasMore(false);
            }
            else {
                aggragateData(data);
            }

            setLoading(false);
        });
    }, [esteira, aggragateData]);

    const submeterParaWorkflow = useCallback(async (
        card: IRelatorio,
        justificativaDetalhada?: string | null,
        planoAcaoCorretiva?: string | null,
        files?: File[],
        automatico?: boolean
    ) => {
        if (!card) return;
        try {
            const body = {
                esteiraId: esteira.id,
                cartaoId: card.id,
                contaContabil: card.contaEmpresa,
                descricaoConta: card.descricaoEmpresa ?? card.descricaoReferencial ?? '',
                saldoContabil: (card as any).saldoFinalReal ?? (card as any).saldoFinal ?? 0,
                saldoConciliado: ((card as any).saldoFinalReal ?? (card as any).saldoFinal ?? 0) * ((card.porcentagemConciliado ?? (card as any).porcentagemConciliacao ?? 0) / 100),
                porcentagem: card.porcentagemConciliado ?? (card as any).porcentagemConciliacao ?? 0,
                isBanco: card.isBanco ?? false,
                isTributo: card.isTributo ?? false,
                isImobilizado: card.isImobilizado ?? false,
                isFornecedor: card.isClienteFornecedor ?? false,
                isCapitalSocial: card.isCapitalSocial ?? false,
                justificativaTecnica: card.descricao ?? null,
                justificativaDetalhada: justificativaDetalhada || null,
                planoAcaoCorretiva: planoAcaoCorretiva || null,
                automatico: automatico ?? false,
            };
            const response = await api.post('workflow/conciliacoes/submeter-motor', body);
            const concilId: number = response.data.concilId;

            if (files && files.length > 0 && concilId) {
                try {
                    const form = new FormData();
                    files.forEach(f => form.append('files', f));
                    await api.post(`workflow/conciliacoes/${concilId}/evidencias`, form, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                } catch {
                    // Evidências são opcionais — falha no upload não cancela a submissão.
                }
            }

            setSoxStatus({
                submetido: true,
                concilId,
                status: 'AGUARDANDO_APROVACAO',
                etapaAtual: response.data.etapa,
                nivelRisco: response.data.nivelRisco,
            });
            Swal.fire({
                text: `Conta ${card.contaEmpresa} movida para aprovação no Workflow SOX. Risco: ${response.data.nivelRisco}`,
                timer: 5000,
                icon: 'success',
                showConfirmButton: false
            });
        } catch (err: any) {
            if (err?.response?.status === 409) {
                Swal.fire({
                    text: 'Esta conta já foi enviada para aprovação no Workflow SOX.',
                    timer: 4000,
                    icon: 'info',
                    showConfirmButton: false
                });
            } else if (err?.response?.status === 403) {
                Swal.fire({
                    text: 'Usuário não cadastrado no módulo Workflow SOX. Acesse o módulo e realize o cadastro.',
                    timer: 6000,
                    icon: 'info',
                    showConfirmButton: false
                });
            } else {
                const errMsg = err?.response?.data?.message
                    || err?.response?.data?.detalhe
                    || (typeof err?.response?.data === 'string' ? err.response.data : null)
                    || `HTTP ${err?.response?.status ?? 'sem resposta'}`;
                Swal.fire({
                    text: `Conta movida para Concluído. Erro ao iniciar o Workflow SOX: ${errMsg}`,
                    timer: 8000,
                    icon: 'warning',
                    showConfirmButton: true,
                    confirmButtonText: 'OK'
                });
            }
        }
    }, [esteira, setSoxStatus]);

    const abrirSeletorTemplate = useCallback((card: IRelatorio, automatico: boolean) => {
        setWfTemplateId(null);
        setWfJustTemplate('');
        setWfTemplateModal({ show: true, card, automatico });
        if (wfTemplates.length === 0) {
            workflowService.listarJustificativaTemplates().then(setWfTemplates).catch(() => setWfTemplates([]));
        }
    }, [wfTemplates]);

    // Botão manual (contas abaixo de 100%, ou reenvio a partir do card) — usa o mesmo
    // catálogo de justificativas do fluxo automático. Só disponível para empresas
    // cadastradas no Workflow SOX, igual ao fluxo automático.
    const abrirEnvioWorkflow = useCallback((card: IRelatorio) => {
        if (!empresaConfiguradaWorkflow) return;
        abrirSeletorTemplate(card, false);
    }, [empresaConfiguradaWorkflow, abrirSeletorTemplate]);

    const handleSelecionarTemplate = useCallback((id: number) => {
        setWfTemplateId(id);
        const tpl = wfTemplates.find(t => t.templateId === id);
        // Opção 10 (Outro) fica em branco — preenchimento manual obrigatório.
        setWfJustTemplate(tpl && tpl.templateId !== 10 ? tpl.justificativaDetalhada : '');
    }, [wfTemplates]);

    const confirmarEnvioComTemplate = useCallback(async () => {
        const card = wfTemplateModal.card;
        if (!card || !wfTemplateId || !wfJustTemplate.trim()) return;
        const tpl = wfTemplates.find(t => t.templateId === wfTemplateId);
        setWfEnviandoTemplate(true);
        try {
            await submeterParaWorkflow(card, wfJustTemplate.trim(), tpl ? JSON.stringify(tpl.pdca) : null, [], wfTemplateModal.automatico);
            setWfTemplateModal({ show: false, card: null, automatico: false });
        } finally {
            setWfEnviandoTemplate(false);
        }
    }, [wfTemplateModal.card, wfTemplateModal.automatico, wfTemplateId, wfJustTemplate, wfTemplates, submeterParaWorkflow]);

    // Entrada automática no Workflow SOX ao mover o card pra "Concluído" — só para
    // empresas já cadastradas/configuradas no módulo (grupo econômico vinculado) E só
    // quando a conta está 100% conciliada. O card pode ser movido pra Concluído com
    // qualquer percentual (isso não muda); se não estiver 100%, o card só move — o
    // analista submete manualmente ao Workflow pelo botão de dentro do card quando quiser.
    const onMovidoParaConcluido = useCallback((codigo: string, _id: number) => {
        if (!empresaConfiguradaWorkflow) return;
        const card = dados?.find(item => item.contaEmpresa === codigo);
        if (!card) return;
        const percentual = card.porcentagemConciliado ?? (card as any).porcentagemConciliacao ?? 0;
        if (percentual !== 100) return;
        abrirSeletorTemplate(card, true);
    }, [empresaConfiguradaWorkflow, dados, abrirSeletorTemplate]);

    useEffect(() => {
        if (!esteira) return;

        // Firestore realtime listener — substitui o hub SignalR legado (nunca implementado no
        // backend, ver ARCHITECTURE.md) para eventos de movimentação de cartão. O `connection`
        // (SignalR) continua sendo repassado ao ModalCard, que o usa para outro evento
        // ("FinalizouProcessamento"), independente deste.
        const unsubscribe = subscribeToEsteira(esteira.id, ({ conta, status, porcentagem, detalhes }) => {
            arrastar(conta, status, porcentagem, false, detalhes);
        });

        return () => unsubscribe();
    }, [esteira, arrastar]);

    useEffect(() => {
        if (esteira && !intervalo) {
            ArquivoCardEsteiraService.buscarNotificacoes(esteira.id).then((resp) => setMensagemSistema(resp));
            const i = setInterval(() => {
                ArquivoCardEsteiraService.buscarNotificacoes(esteira.id).then((resp) => setMensagemSistema(resp));
            }, 600000);

            setIntervalo(i);
        }


        return () => {
            clearInterval(intervalo);
        }
    }, [esteira, intervalo]);

    return (
        <div className='row'>
            <div className='col-md-12 mt-2'>
                <div className='row'>
                    <div className='col-md-3 form-group'>
                        <label>{t('De: ')} </label>
                        <select disabled={loadingMovimentando} className='form-control' value={movimentarDe} onChange={(e) => setMovimentarDe(e.target.value)}>
                            <option value=''>{t('Selecione uma opção')}</option>
                            <option value='Arquivado'>{t('Arquivado')}</option>
                            <option value='Pendentes'>{t('Pendentes')}</option>
                            <option value='Em processamento'>{t('Análise Automática')}</option>
                            <option value='Conciliado Parcial'>{t('Análise Parcial')}</option>
                            <option value='Em Conferencia'>{t('Em Conferência')}</option>
                            <option value='Concluida'>{t('Concluído')}</option>
                        </select>
                    </div>
                    <div className='col-md-3 form-group'>
                        <label>{t('Para: ')}</label>
                        <select disabled={loadingMovimentando} className='form-control' value={movimentarPara} onChange={(e) => setMovimentarPara(e.target.value)}>
                            <option value=''>{t('Selecione uma opção')}</option>
                            <option value='Pendentes'>{t('Pendentes')}</option>
                            <option value='Em processamento'>{t('Análise Automática')}</option>
                            <option value='Conciliado Parcial'>{t('Análise Parcial')}</option>
                            <option value='Em Conferencia'>{t('Em Conferência')}</option>
                            <option value='Concluida'>{t('Concluído')}</option>
                        </select>
                    </div>
                    <div className='col-md-3 form-group'>
                        <label>{t("Grupo")}: </label>
                        <select disabled={loadingMovimentando} className='form-control' value={grupo} onChange={(e) => setGrupo(e.target.value)}>
                            <option value=''>{t("Selecione uma opção")}</option>
                            <option value='todas'>{t("Todos")}</option>
                            <option value='todas-100'>{t("Todos")} 100%</option>
                            <option value='clienteFornecedor'>{t("Cliente/Fornecedor")}</option>
                            <option value='cliente'>{t("Somente Cliente")}</option>
                            <option value='fornecedor'>{t("Somente Fornecedor")}</option>
                            <option value='tributo'>{t("Tributos")}</option>
                            <option value='imobilizado'>{t("Imobilizado")}</option>
                            <option value='pl'>PL</option>
                            <option value='emprestimo'>{t("Empréstimo")}</option>
                            <option value='banco'>{t("Banco e Caixa")}</option>
                            <option value='somentebanco'>{t("Somente Banco")}</option>
                            <option value='somentecaixa'>{t("Somente Caixa")}</option>
                            <option value='folha'>{t("Folha de pagamento")}</option>
                            <option value='estoque'>{t("Estoque")}</option>
                        </select>
                    </div>
                    <div className='col-md-3 mt-6'>
                        <button disabled={loadingMovimentando} className='btn btn-success' onClick={movimentarGrupo}>{t("Movimentar")}</button>
                    </div>
                </div>
            </div>


            <div className='col-md-12 mt-2'>
                <div className='row'>
                    <div className='col-md-6 form-group'>
                        <label>{t("Filtro")}:</label>
                        <input disabled={carregandoFiltro} type='text' placeholder={t('Digite aqui um filtro...')} value={filtro} onChange={(e) => setFiltro(e.target.value)} className='form-control' />
                    </div>
                    <div className='col-md-4'>
                        <button disabled={carregandoFiltro} className='btn btn-success mt-6' onClick={() => onSearch()}>{t("Buscar fora da tela")}</button>
                    </div>
                </div>
            </div>

            <ModalCard
                connection={connection}
                mostrarContaReferencial
                esteira={esteira.id}
                empresa={empresaID}
                dataI={esteira?.periodoInicio || new Date()}
                dataF={esteira?.periodoFim || new Date()}
                contas={dados}
                updateAll={arrastar}
                mensagemSistema={mensagemSistema}
                showModal={showModal}
                toggleModal={() => {
                    setShowModal({
                        show: false,
                        data: null,
                        reprocessar: null
                    });
                }} updateDescription={modal.updateDescription} show={modal.show} acc={modal.data} reprocessar={modal.reprocessar} conciliacaoInteligente={modal.conciliacaoInteligente} processou conferencia={modal?.data?.status === 'Em Conferencia'} onRequestExtratoBancario={(item: any) => setModalExtratoBancario({ show: true, cartaoId: item.id, cartaoCodigo: item.contaEmpresa })} onRequestFolhaPagamento={(item: any) => setModalFolhaPagamento({ show: true, cartaoId: item.id, cartaoCodigo: item.contaEmpresa })} onConcluidoClick={async () => {
                    const card = modal.data;
                    if (!card) return;
                    // Mesmo caminho do arrastar para a coluna Concluído (colunaBoard): permissão, PATCH v3
                    // Cartao/{id}/movimentar (grava status + MOVE_CARD) e Workflow SOX automático só a 100%.
                    if (usuario?.perfil !== 'Administrador' && !usuario?.responsavelContabil) {
                        Swal.fire({
                            text: 'Apenas administradores ou responsável contábil podem mover para Concluido!!',
                            timer: 3000,
                            icon: 'error',
                            showConfirmButton: false
                        });
                        return;
                    }
                    try {
                        await RelatoriosService.newStatusConta(card.id, esteira.id, 'Concluida');
                    } catch (err: any) {
                        Swal.fire({
                            text: err?.response?.data?.message || 'Não foi possível mover o cartão para Concluído.',
                            timer: 6000,
                            icon: 'error',
                            showConfirmButton: false
                        });
                        return;
                    }
                    arrastar(card.contaEmpresa, 'Concluida', null);
                    setShowModal({ show: false, data: null, reprocessar: null });
                    setTimeout(() => onMovidoParaConcluido(card.contaEmpresa, card.id), 150);
                }} soxStatus={soxStatus} onSubmeterSox={() => {
                    const card = modal.data;
                    if (!card) return;
                    setShowModal({ show: false, data: null, reprocessar: null });
                    setTimeout(() => abrirEnvioWorkflow(card), 150);
                }} />

            <ModalExtratoBancario
                show={modalExtratoBancario.show}
                esteiraId={esteira.id}
                cartaoId={modalExtratoBancario.cartaoId}
                onHide={() => setModalExtratoBancario({ show: false, cartaoId: 0, cartaoCodigo: '' })}
                onSuccess={() => arrastar(modalExtratoBancario.cartaoCodigo, 'Em processamento', null)}
            />

            <ModalFolhaPagamento
                show={modalFolhaPagamento.show}
                esteiraId={esteira.id}
                cartaoId={modalFolhaPagamento.cartaoId}
                onHide={() => setModalFolhaPagamento({ show: false, cartaoId: 0, cartaoCodigo: '' })}
                onSuccess={() => arrastar(modalFolhaPagamento.cartaoCodigo, 'Em processamento', null)}
            />

            <div className='col-md-12 mt-2 scrollsize'>
                <DndProvider backend={HTML5Backend}>
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Arquivado' || movimentarPara === 'Arquivado')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Arquivado').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial={mostrarReferencial} esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} onConciliationInteligent={changeRouteConciliation} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Rascunho")} status="Arquivado" />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Pendentes' || movimentarPara === 'Pendentes')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Pendentes').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial={mostrarReferencial} esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} onConciliationInteligent={changeRouteConciliation} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Pendentes")} status="Pendentes" />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Em processamento' || movimentarPara === 'Em processamento')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Em processamento' || f.status === 'Capturado Fila').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial={mostrarReferencial} esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} onConciliationInteligent={changeRouteConciliation} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Conciliação Automática")} status="Em processamento" onRequestExtratoBancario={(item: any) => setModalExtratoBancario({ show: true, cartaoId: item.id, cartaoCodigo: item.codigo })} onRequestFolhaPagamento={(item: any) => setModalFolhaPagamento({ show: true, cartaoId: item.id, cartaoCodigo: item.codigo })} />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Conciliado Parcial' || movimentarPara === 'Conciliado Parcial')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Conciliado Parcial').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial={mostrarReferencial} esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} onConciliationInteligent={changeRouteConciliation} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Conciliado Parcial")} status="Conciliado Parcial" />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Conciliacao' || movimentarPara === 'Conciliacao')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Conciliacao').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial={mostrarReferencial} esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} onConciliationInteligent={changeRouteConciliation} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Conciliação Inteligente")} status="Conciliacao" />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Em Conferencia' || movimentarPara === 'Em Conferencia')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Em Conferencia').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial={mostrarReferencial} esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} onConciliationInteligent={changeRouteConciliation} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Em Conferência")} status="Em Conferencia" />
                    <ColunaBoard showModal={showModal} loadingMovimentando={loadingMovimentando && (movimentarDe === 'Concluida' || movimentarPara === 'Concluida')} possuiFiltro={!!filtro} loadDataReport={loadDataReport} dados={dados.filter(f => f.status === 'Concluida').filter(f => filtroTexto(f))} setDados={setDados} mostrarContaReferencial={mostrarReferencial} esteira={esteira} empresaID={empresaID} perfil={usuario?.perfil} responsavelContabil={usuario?.responsavelContabil || false} onConciliationInteligent={changeRouteConciliation} updateAll={arrastar} conciliando={conciliando} setConciliando={setConciliando} mensagemSistema={mensagemSistema} esteiraID={esteira.id} title={t("Concluído")} status="Concluida" onMovidoParaConcluido={onMovidoParaConcluido} />
                </DndProvider>
            </div>

            <WfTemplateModal
                show={wfTemplateModal.show}
                card={wfTemplateModal.card}
                templates={wfTemplates}
                templateId={wfTemplateId}
                justificativa={wfJustTemplate}
                enviando={wfEnviandoTemplate}
                onSelecionar={handleSelecionarTemplate}
                onJustificativaChange={setWfJustTemplate}
                onConfirm={confirmarEnvioComTemplate}
                onCancel={() => setWfTemplateModal({ show: false, card: null, automatico: false })}
            />
        </div>
    )
}

function WfTemplateModal({ show, card, templates, templateId, justificativa, enviando, onSelecionar, onJustificativaChange, onConfirm, onCancel }: {
    show: boolean;
    card: IRelatorio | null;
    templates: JustificativaTemplate[];
    templateId: number | null;
    justificativa: string;
    enviando: boolean;
    onSelecionar: (id: number) => void;
    onJustificativaChange: (v: string) => void;
    onConfirm: () => void;
    onCancel: () => void;
}) {
    if (!show || !card) return null;
    const templateSelecionado = templates.find(t => t.templateId === templateId);
    const isOutro = templateSelecionado?.templateId === 10;
    const podeConfirmar = !!templateId && justificativa.trim().length > 0 && !enviando;
    return createPortal(
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 99999,
                background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '16px'
            }}
            onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
        >
            <div style={{
                background: '#fff', borderRadius: '12px',
                width: '100%', maxWidth: '560px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                display: 'flex', flexDirection: 'column'
            }}>
                <div style={{ padding: '20px 24px 12px', borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h5 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#212529' }}>
                        Enviar para Workflow SOX
                    </h5>
                    <button
                        onClick={onCancel}
                        style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6c757d', lineHeight: 1, padding: '0 4px' }}
                    >×</button>
                </div>
                <div style={{ padding: '16px 24px', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                    <p style={{ color: '#6c757d', fontSize: '13px', marginBottom: '16px' }}>
                        Conta: <strong style={{ color: '#212529' }}>{card.contaEmpresa}</strong>
                    </p>

                    <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>
                        Justificativa e plano de melhoria pré-definidos
                    </label>
                    <select
                        value={templateId ?? ''}
                        onChange={e => onSelecionar(Number(e.target.value))}
                        style={{ width: '100%', boxSizing: 'border-box', marginBottom: '14px', border: '1px solid #ced4da', borderRadius: '6px', padding: '8px 10px', fontSize: '13px' }}
                    >
                        <option value='' disabled>— Selecione uma opção —</option>
                        {templates.map(t => <option key={t.templateId} value={t.templateId}>{t.templateId} — {t.titulo}</option>)}
                    </select>

                    {templateSelecionado && !isOutro && (
                        <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '8px', padding: '10px 12px', marginBottom: '14px', fontSize: '12px', color: '#495057' }}>
                            <strong style={{ display: 'block', marginBottom: 4 }}>Plano de melhoria (PDCA) — pré-preenchido:</strong>
                            {(['plan', 'do', 'check', 'act'] as const).map(fase => (
                                <div key={fase} style={{ marginTop: 4 }}>
                                    <strong style={{ textTransform: 'uppercase' }}>{fase}</strong>: 👤 {templateSelecionado.pdca[fase].pessoas} · ⚙️ {templateSelecionado.pdca[fase].processos} · 💻 {templateSelecionado.pdca[fase].tecnologia}
                                </div>
                            ))}
                            <span style={{ display: 'block', marginTop: 6, color: '#6c757d' }}>Poderá ser detalhado/editado depois, na tela do Workflow SOX.</span>
                        </div>
                    )}

                    <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', marginBottom: '4px' }}>
                        Justificativa detalhada
                        {isOutro && <span style={{ color: '#dc3545', fontWeight: 400 }}> (obrigatória)</span>}
                    </label>
                    <textarea
                        value={justificativa}
                        onChange={e => onJustificativaChange(e.target.value)}
                        placeholder={isOutro ? 'Descreva a causa, impacto, responsável e ação necessária...' : 'Descreva a situação da conta...'}
                        rows={4}
                        style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', border: '1px solid #ced4da', borderRadius: '6px', padding: '8px 10px', fontSize: '13px', fontFamily: 'inherit' }}
                    />
                </div>
                <div style={{ padding: '12px 24px 20px', borderTop: '1px solid #e9ecef', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button
                        onClick={onCancel}
                        disabled={enviando}
                        style={{ padding: '8px 18px', border: '1px solid #ced4da', borderRadius: '6px', background: '#fff', color: '#495057', fontSize: '14px', cursor: 'pointer' }}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={!podeConfirmar}
                        style={{ padding: '8px 18px', border: 'none', borderRadius: '6px', background: '#5E6397', color: '#fff', fontSize: '14px', cursor: podeConfirmar ? 'pointer' : 'not-allowed', opacity: podeConfirmar ? 1 : 0.6 }}
                    >
                        {enviando ? 'Enviando...' : 'Enviar para Workflow'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default Board;